import React, { useState } from 'react';
import { RenovationInputs, CalculationResult, RenovationService } from './types/renovation';
import { calculateRenovationCosts } from './utils/calculateCosts';
import { InputForm } from './components/InputForm';
import { Logo } from './components/Logo';
import { LanguageToggle } from './components/LanguageToggle';
import { ContactForm } from './components/ContactForm';
import { RENOVATION_RATES } from './utils/calculationRates';
import { Language, translations } from './types/language';
import { UserData } from './types/user';
import { DataService } from './services/DataService';
import { Footer } from './components/Footer';
import { AppError } from './services/errorHandling';

function App() {
  const [language, setLanguage] = useState<Language>('fr');
  const [showContactForm, setShowContactForm] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputs, setInputs] = useState<RenovationInputs>({
    services: Object.keys(RENOVATION_RATES).reduce((acc, key) => ({
      ...acc,
      [key]: {
        id: key,
        enabled: false,
        area: 0
      }
    }), {} as Record<string, RenovationService>)
  });

  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleInputChange = (services: Record<string, RenovationService>) => {
    setInputs({ services });
    setResult(null);
    setShowResults(false);
    setError(null);
  };

  const handleCalculate = () => {
    const calculationResult = calculateRenovationCosts(inputs);
    setResult(calculationResult);
    setShowContactForm(true);
    setShowResults(false);
    setError(null);
  };

  const handleContactFormSubmit = async (data: UserData) => {
    try {
      if (!result) {
        throw new Error('Calculation result is missing');
      }

      await DataService.saveUserData({
        userData: data,
        breakdown: result.breakdown,
        totalCost: result.totalCost,
        language
      });

      setShowResults(true);
      setError(null);
    } catch (error) {
      console.error('Failed to save user data:', error);
      if (error instanceof AppError) {
        setError(error.getLocalizedMessage(language));
      } else {
        setError(language === 'fr' 
          ? 'Une erreur inattendue s\'est produite. Veuillez réessayer.'
          : 'An unexpected error occurred. Please try again.');
      }
      throw error;
    }
  };

  const t = translations[language];

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <Logo translations={t} />
          <LanguageToggle
            currentLanguage={language}
            onLanguageChange={setLanguage}
          />
        </div>

        <div className="space-y-8">
          <InputForm
            inputs={inputs}
            onChange={handleInputChange}
            onCalculate={handleCalculate}
            translations={t}
          />
        </div>
      </div>

      {showContactForm && result && (
        <ContactForm
          onSubmit={handleContactFormSubmit}
          onClose={() => {
            setShowContactForm(false);
            setShowResults(false);
            setError(null);
          }}
          translations={t}
          showResults={showResults}
          breakdown={result.breakdown}
          totalCost={result.totalCost}
          error={error}
        />
      )}

      <Footer language={language} />
    </div>
  );
}

export default App;